import { escHtml } from './registry';

/**
 * 统一打印出口：隐藏 iframe 里装上内容后调浏览器自己的 window.print()，不经过服务端。
 * - pdf：把字节交给浏览器内置 PDF 查看器打印（保真、分页准确）
 * - html：各引擎把渲染结果（HTML + 专用 CSS）交过来，在 iframe 里排版后打印
 */
export type PrintPayload =
  | { kind: 'pdf'; bytes: ArrayBuffer | Uint8Array }
  | { kind: 'html'; html: string; css?: string };

const BASE_CSS = `
  body { margin: 0; font-family: "Hiragino Sans", "Yu Gothic", "Meiryo", "PingFang SC", sans-serif; color: #111; }
  pre { white-space: pre-wrap; font: 12px/1.7 Menlo, Consolas, monospace; margin: 16mm; }
  table { border-collapse: collapse; }
`;

export const printPayload = async (payload: PrintPayload, title: string): Promise<void> => {
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0';
  document.body.appendChild(frame);
  // 打印对话框是模态的，关掉后才会回到这里；留足时间再回收 iframe 和 blob
  const cleanup = (url?: string) =>
    setTimeout(() => {
      if (url) URL.revokeObjectURL(url);
      frame.remove();
    }, 60_000);

  if (payload.kind === 'pdf') {
    const url = URL.createObjectURL(new Blob([payload.bytes as BlobPart], { type: 'application/pdf' }));
    frame.onload = () => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      cleanup(url);
    };
    frame.src = url;
    return;
  }

  const doc = frame.contentDocument;
  if (!doc) {
    frame.remove();
    return;
  }
  doc.open();
  doc.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${escHtml(title)}</title><style>${BASE_CSS}${
      payload.css || ''
    }</style></head><body>${payload.html}</body></html>`
  );
  doc.close();
  // 等图片都到位再打印，否则打出来是空框
  await Promise.all(
    Array.from(doc.images).map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
    )
  );
  frame.contentWindow?.focus();
  frame.contentWindow?.print();
  cleanup();
};
